import { useState } from "react"
import { Field, ErrorMessage, getIn, useFormikContext } from "formik"
import Select from "react-select"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { formatNumberWithCommas, processDecimalInput } from "../../../utils/formatNumber"
import { currencyOptions } from "../../../utils/currencyOptions"
import countryCodes from "../../../utils/countryCodes.json"

const formatDate = (date) => {
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

// Error priority order: required > typeError > match/pattern > min > max > other
const getPriorityError = (error) => {
  if (!error) return null
  
  // Handle array of errors (take first one)
  if (Array.isArray(error)) {
    error = error[0]
  }
  
  if (typeof error === 'object' && error !== null) {
    // Get first error key based on priority
    const priorityKeys = ['required', 'typeError', 'matches', 'min', 'max']
    for (const key of priorityKeys) {
      if (error[key]) return error[key]
    }
    // Return first available error
    const firstKey = Object.keys(error)[0]
    return error[firstKey]
  }
  
  return error
}

// Check if field has a meaningful value
const hasFieldValue = (value) => {
  return value !== undefined && value !== null && value !== ""
}

const DynamicFormField = ({ field, values, setFieldValue, errors, touched, readOnly = false }) => {
  // Use Formik context directly for correct methods
  const { setFieldTouched, validateForm } = useFormikContext()
  const { name, type, label, placeholder, required, colClass, options, minDate, maxDate, showMonthDropdown, showYearDropdown, currencyField, rows, maxLength } = field

  // State for currency field focus
  const [isCurrencyFocused, setIsCurrencyFocused] = useState(false)

  // Use getIn to handle dot notation properly
  const fieldValue = getIn(values, name)
  const fieldError = getIn(errors, name)
  const isTouched = getIn(touched, name)

  // Get the single priority error to display
  const displayError = isTouched ? getPriorityError(fieldError) : null

  // Check if we should show required error
  const shouldShowRequiredError = () => {
    if (!displayError) return false
    
    // Don't show required error if field has value
    if (hasFieldValue(fieldValue)) {
      const isRequiredError = /is required|required/i.test(displayError)
      if (isRequiredError) return false
    }
    
    return true
  }

  const showError = shouldShowRequiredError()
  const errorClass = showError ? "is-invalid" : ""

  // Helper function for select field change with validation
  const handleSelectChange = (option) => {
    const value = option ? option.value : ""
    setFieldValue(name, value)
    setFieldTouched(name, true)
    // Call validateForm immediately without setTimeout
    validateForm()
  }

  // Helper function for text/number change with validation
  const handleTextChange = (e) => {
    setFieldValue(name, e.target.value)
    setFieldTouched(name, true)
    // Call validateForm immediately without setTimeout
    validateForm()
  }

  // Render error message
  const renderError = () => {
    if (!showError || !displayError) return null
    return <div className={type === 'select' || type === 'countrySelect' ? "text-danger small" : "invalid-feedback"}>{displayError}</div>
  }

  // Render different field types
  const renderField = () => {
    switch (type) {
      case "text":
        return (
          <>
            <Field
              type="text"
              name={name}
              placeholder={placeholder}
              className={`form-control ${errorClass}`}
              readOnly={readOnly}
              onChange={(e) => handleTextChange(e)}
              onPaste={(e) => {
                e.preventDefault()
                // For text fields, allow alphanumeric and common punctuation but block excessive dots/decimal points
                const pastedData = e.clipboardData.getData('text')
                // Remove multiple consecutive dots and limit decimal places
                let cleaned = pastedData.replace(/\.+/g, '.').replace(/^\.|\.$/g, '')
                const parts = cleaned.split('.')
                if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('')
                if (parts.length === 2 && parts[1].length > 2) cleaned = parts[0] + '.' + parts[1].slice(0, 2)
                setFieldValue(name, cleaned)
                setFieldTouched(name, true)
                validateForm()
              }}
            />
            {renderError()}
          </>
        )

      case "percentage":
        // For percentage fields like purity (0-100 with max 2 decimal places)
        return (
          <>
            <Field
              type="text"
              name={name}
              placeholder={placeholder}
              className={`form-control ${errorClass}`}
              readOnly={readOnly}
              value={fieldValue || ''}
              onChange={(e) => {
                // Allow digits, decimal point, and % sign
                let cleaned = e.target.value.replace(/[^\d.%]/g, '')
                // Handle multiple % signs - keep only the last one at the end
                const percentCount = (cleaned.match(/%/g) || []).length
                if (percentCount > 1) {
                  cleaned = cleaned.replace(/%/g, '') + '%'
                }
                // Limit to 2 decimal places
                const parts = cleaned.split('.')
                if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('')
                if (parts.length === 2 && parts[1].length > 2) cleaned = parts[0] + '.' + parts[1].slice(0, 2)
                setFieldValue(name, cleaned || '')
                setFieldTouched(name, true)
                validateForm()
              }}
              onPaste={(e) => {
                e.preventDefault()
                const pastedData = e.clipboardData.getData('text')
                // Allow digits, decimal point, and % sign
                let cleaned = pastedData.replace(/[^\d.%]/g, '')
                // Handle multiple % signs - keep only the last one at the end
                const percentCount = (cleaned.match(/%/g) || []).length
                if (percentCount > 1) {
                  cleaned = cleaned.replace(/%/g, '') + '%'
                }
                // Limit to 2 decimal places
                const parts = cleaned.split('.')
                if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('')
                if (parts.length === 2 && parts[1].length > 2) cleaned = parts[0] + '.' + parts[1].slice(0, 2)
                setFieldValue(name, cleaned || '')
                setFieldTouched(name, true)
                validateForm()
              }}
            />
            {renderError()}
          </>
        )

      case "select":
        return (
          <>
            <Select
              options={options}
              value={options.find((option) => option.value === fieldValue)}
              onChange={(option) => handleSelectChange(option)}
              onBlur={() => setFieldTouched(name, true)}
              className="react-select-container"
              classNamePrefix="react-select"
              isDisabled={readOnly}
              // Type to filter, and render the menu above the sticky form footer so the last
              // options can be tapped (ML-34).
              isSearchable
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPlacement="auto"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
              placeholder={placeholder}
            />
            {renderError()}
          </>
        )

      case "countrySelect":
        const countryOptions = countryCodes.map(c => ({
          value: c.name,
          label: `${c.flag ? c.flag + ' ' : ''}${c.name}`
        }))
        return (
          <>
            <Select
              options={countryOptions}
              value={countryOptions.find((option) => option.value === fieldValue) || null}
              onChange={(option) => {
                const value = option ? option.value : ''
                setFieldValue(name, value)
                setFieldTouched(name, true)
                validateForm()
              }}
              onBlur={() => setFieldTouched(name, true)}
              placeholder="Select country"
              className="react-select-container"
              classNamePrefix="react-select"
              isSearchable
              isDisabled={readOnly}
            />
            {renderError()}
          </>
        )

      case "currency": {
        const showCurrencyLabel = name === 'estimatedValue' || name === 'estimatedCurrentValue' || name === 'currentMarketPrice' || name === 'currentEstimateValue' || name === 'ownershipPercentage' || name === 'rentalIncome.amount' || name === 'premiumAmount'
        const focusedCurrencyValue = fieldValue ? String(parseFloat(parseFloat(fieldValue).toFixed(2))) || "" : ""

        return (
          <>
            <div className="d-flex align-items-center">
              {showCurrencyLabel && values.purchaseCurrency && (
                <div className="selected-currency-label me-2 text-sm border-bottom py-1.5 lh-base">
                  {values.purchaseCurrency}
                </div>
              )}
              <div className="input-group flex-grow-2">
                <Field
                  type="text"
                  name={name}
                  className={`form-control ${showError ? 'is-invalid' : ''}`}
                  placeholder={placeholder}
                  readOnly={readOnly}
                  value={isCurrencyFocused ? focusedCurrencyValue : (fieldValue ? formatNumberWithCommas(fieldValue) : "")}
                  onFocus={() => setIsCurrencyFocused(true)}
                  onBlur={() => setIsCurrencyFocused(false)}
                  onChange={(e) => {
                    const unformatted = processDecimalInput(e.target.value)
                    setFieldValue(name, unformatted || "")
                    setFieldTouched(name, true)
                    validateForm()
                  }}
                  onPaste={(e) => {
                    e.preventDefault()
                    const unformatted = processDecimalInput(e.clipboardData.getData('text'))
                    setFieldValue(name, unformatted || "")
                    setFieldTouched(name, true)
                    validateForm()
                  }}
                />
              </div>
            </div>
            {renderError()}
          </>
        )
      }

      case "currencyWithSelect": {
        const focusedCurrencyWithSelectValue = fieldValue ? String(parseFloat(parseFloat(fieldValue).toFixed(2))) || "" : ""
        return (
          <>
            <div className="input-group">
              <Field
                as="select"
                name={currencyField}
                className="form-control"
                readOnly={readOnly}
                disabled={readOnly}
                style={{ maxWidth: '60px' }}
              >
                {currencyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Field>
              <Field
                type="text"
                name={name}
                className={`form-control pl ${errorClass}`}
                placeholder={placeholder}
                readOnly={readOnly}
                value={isCurrencyFocused ? focusedCurrencyWithSelectValue : (fieldValue ? formatNumberWithCommas(fieldValue) : "")}
                onFocus={() => setIsCurrencyFocused(true)}
                onBlur={() => setIsCurrencyFocused(false)}
                onChange={(e) => {
                  const unformatted = processDecimalInput(e.target.value)
                  setFieldValue(name, unformatted || "")
                  setFieldTouched(name, true)
                  validateForm()
                }}
                onPaste={(e) => {
                  e.preventDefault()
                  const unformatted = processDecimalInput(e.clipboardData.getData('text'))
                  setFieldValue(name, unformatted || "")
                  setFieldTouched(name, true)
                  validateForm()
                }}
              />
            </div>
            {renderError()}
          </>
        )
      }

      case "date":
        // Handle minDate reference
        let minDateValue = null
        if (minDate && typeof minDate === "string") {
          const minDateFieldValue = getIn(values, minDate)
          if (minDateFieldValue) {
            minDateValue = new Date(minDateFieldValue)
          }
        } else if (minDate instanceof Date) {
          minDateValue = minDate
        }

        return (
          <>
            <Field name={name}>
              {({ field: formikField, form }) => (
                <DatePicker
                  selected={formikField.value ? new Date(formikField.value) : null}
                  onChange={(date) =>
                    form.setFieldValue(name, date ? formatDate(date) : "")
                  }
                  onBlur={() => form.setFieldTouched(name, true)}
                  minDate={minDateValue}
                  maxDate={maxDate}
                  dateFormat="yyyy-MM-dd"
                  className={`form-control ${errorClass}`}
                  placeholderText={placeholder || "Select date"}
                  showMonthDropdown={showMonthDropdown}
                  showYearDropdown={showYearDropdown}
                  dropdownMode="select"
                  readOnly={readOnly}
                  disabled={readOnly}
                />
              )}
            </Field>
            {renderError()}
          </>
        )

      case "number": {
        const rawNum = parseFloat(fieldValue)
        const displayNum = !isNaN(rawNum)
          ? (String(fieldValue).includes('.') ? parseFloat(rawNum.toFixed(2)) : rawNum)
          : fieldValue
        return (
          <>
            <Field
              type="number"
              step="any"
              name={name}
              placeholder={placeholder}
              className={`form-control ${errorClass}`}
              readOnly={readOnly}
              value={displayNum ?? ""}
              onChange={(e) => {
                setFieldValue(name, e.target.value)
                setFieldTouched(name, true)
                validateForm()
              }}
            />
            {renderError()}
          </>
        )
      }

      case "textarea":
        return (
          <>
            <Field
              as="textarea"
              name={name}
              placeholder={placeholder}
              className={`form-control ${errorClass}`}
              rows={rows || 3}
              maxLength={maxLength}
              readOnly={readOnly}
              onChange={(e) => {
                setFieldValue(name, e.target.value)
                setFieldTouched(name, true)
                validateForm()
              }}
            />
            {renderError()}
          </>
        )

      case "checkbox":
        return (
          <>
            <Field type="checkbox" name={name} className="form-check-input" disabled={readOnly} />
            {renderError()}
          </>
        )

      case "radio":
        return (
          <>
            <div className="radio-group">
              {options && options.map((option) => (
                <div key={option.value} className="form-check">
                  <Field
                    type="radio"
                    name={name}
                    value={option.value}
                    className="form-check-input"
                    id={`${name}-${option.value}`}
                    disabled={readOnly}
                    onChange={() => {
                      setFieldValue(name, option.value)
                      setFieldTouched(name, true)
                      validateForm()
                    }}
                  />
                  <label className="form-check-label" htmlFor={`${name}-${option.value}`}>
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            {renderError()}
          </>
        )

      default:
        return null
    }
  }

  return (
    <div className={colClass || "col-md-12 mb-4"}>
      <label className="form-label">
        {label}
        {required && <span className="text-danger">*</span>}
      </label>
      {renderField()}
    </div>
  )
}

export default DynamicFormField
